import React, { useState, useContext, useEffect } from "react";
import apiClient from "../api/client";
import { ArrowLeft, Eye, MapPin, Heart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { markFlowStep } from "../utils/unityFlowStorage";
import LockedFeatureCard from "./common/LockedFeatureCard";
import { useEntitlementsSafe } from "../context/EntitlementsContext";

// Helper to extract clean error message from various error formats
const extractErrorMessage = (error) => {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  if (data?.detail) {
    if (typeof data.detail === "string") return data.detail;
    if (Array.isArray(data.detail)) {
      const msgs = data.detail.map((d) => d.msg || JSON.stringify(d));
      return msgs.join(", ");
    }
    return JSON.stringify(data.detail);
  }
  if (error?.message) return error.message;
  return "Something went wrong. Please try again.";
};

const WhoLikedMe = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  const { hasFeature } = useEntitlementsSafe();

  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [likingInProgress, setLikingInProgress] = useState(new Set());
  const [errors, setErrors] = useState({});

  const canViewWhoLiked = hasFeature("can_see_who_liked");

  useEffect(() => {
    markFlowStep("checkedLikes");

    if (!canViewWhoLiked) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const profilesResponse = await apiClient.get(`/profiles/who_liked_me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setProfiles(Array.isArray(profilesResponse.data) ? profilesResponse.data : []);

        // Clear likes badge when viewing page
        try {
          await apiClient.post(
            "/notifications/clear",
            { type: "likes" },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (window.refreshNotificationSummary) {
            window.refreshNotificationSummary();
          }
        } catch (clearErr) {
          console.warn("[WhoLikedMe] Failed to clear likes badge:", clearErr?.message || clearErr);
        }
      } catch (error) {
        // Backend will enforce access; treat 403 as locked state
        if (error?.response?.status === 403) return;
        console.error("[WhoLikedMe] Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, canViewWhoLiked]);

  const handleLikeBack = async (profileUserId) => {
    if (likingInProgress.has(profileUserId)) return;

    setLikingInProgress((prev) => new Set(prev).add(profileUserId));
    setErrors((prev) => ({ ...prev, [profileUserId]: null }));

    try {
      const response = await apiClient.post(
        `/matches/like/${profileUserId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setProfiles((prev) =>
        prev.filter((p) => (p.user_id || p.profile?.user_id) !== profileUserId)
      );

      if (response.data?.matched && response.data?.conversation_id) {
        alert("It's a match! Say hello");
        navigate(`/inbox?conversationId=${response.data.conversation_id}`);
      } else {
        alert("You liked them back! They'll see you in their matches if they like you too.");
      }
    } catch (error) {
      console.error("Failed to like back:", error);
      setErrors((prev) => ({
        ...prev,
        [profileUserId]: extractErrorMessage(error),
      }));
    } finally {
      setLikingInProgress((prev) => {
        const next = new Set(prev);
        next.delete(profileUserId);
        return next;
      });
    }
  };

  // --- UI states ---
  if (!canViewWhoLiked) {
    return (
      <LockedFeatureCard
        title="See Who Liked You"
        subtitle="This feature is available on Elite tiers."
        buttonText="Upgrade"
        onBack={() => navigate("/dashboard")}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1220] text-white flex items-center justify-center">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0b1220] to-[#0b1220] text-white">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/dashboard")}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15"
          >
            <ArrowLeft size={18} />
            Back
          </button>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Eye size={20} /> Who Liked You
          </h1>
        </div>

        {profiles.length === 0 ? (
          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            No likes yet. Check back soon
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {profiles.map((p) => {
              const profile = p.profile || p;
              const userId = p.user_id || profile.user_id;

              const nameRaw =
                profile.display_name || profile.name || profile.first_name || "Match";
              const name =
                ["none", "null", "undefined"].includes(String(nameRaw).toLowerCase())
                  ? "Match"
                  : nameRaw;

              const photo =
                profile.primary_photo_url || profile.photo_url || profile.avatar_url || "";

              const location =
                profile.location ||
                (profile.city && profile.state ? `${profile.city}, ${profile.state}` : "");

              return (
                <div key={userId} className="rounded-2xl bg-white/5 border border-white/10 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-full bg-white/10 overflow-hidden flex items-center justify-center">
                      {photo ? (
                        <img src={photo} alt={name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-lg font-semibold">{name.charAt(0)}</span>
                      )}
                    </div>

                    <div className="min-w-0">
                      <div className="font-semibold truncate">{name}</div>
                      {location ? (
                        <div className="text-sm text-white/70 flex items-center gap-1">
                          <MapPin size={14} />
                          <span className="truncate">{location}</span>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <button
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-pink-600 hover:bg-pink-500 disabled:opacity-60"
                    onClick={() => handleLikeBack(userId)}
                    disabled={likingInProgress.has(userId)}
                  >
                    <Heart size={16} />
                    {likingInProgress.has(userId) ? "Liking..." : "Like Back"}
                  </button>

                  {errors[userId] ? (
                    <div className="mt-3 text-sm text-red-300">{errors[userId]}</div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WhoLikedMe;
